import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Cpu,
  MemoryStick,
  Gauge,
  HardDrive,
  Power,
  PowerOff,
  RotateCcw,
  RefreshCw,
  Copy,
  Check,
  Clock,
  CloudUpload,
  CircleDollarSign,
} from 'lucide-react';
import { OsIcon } from '../lib/osIcons';
import type { PowerAction } from '@neburst-bot/shared';
import {
  useInstance,
  useInstanceAction,
  useInstanceBilling,
  useInstanceMetrics,
  useInstanceShape,
  useMetricsHistory,
} from '../api/hooks';
import {
  Button,
  Cell,
  ProgressBar,
  Section,
  Segmented,
  Skeleton,
  StatusDot,
} from '../components/ui';
import { SparkChart } from '../components/Chart';
import { getTg } from '../lib/tg';
import { useT, useLocale } from '../i18n';
import { statusLabel } from '../lib/status';

type ChartTab = 'live' | 'cpu' | 'memory' | 'bandwidth';

export function InstanceDetail() {
  const { uuid = '' } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale();
  const inst = useInstance(uuid);
  const metrics = useInstanceMetrics(uuid);
  const shape = useInstanceShape(uuid);
  const billing = useInstanceBilling(uuid);
  // Use 1-hour window for live rolling samples (dashboard convention).
  const history = useMetricsHistory(uuid, 1);
  const action = useInstanceAction(uuid);
  const [chartTab, setChartTab] = useState<ChartTab>('cpu');

  const trigger = (a: PowerAction) => {
    const tg = getTg();
    const run = () => {
      action.mutate(a, {
        onSuccess: () => tg?.HapticFeedback.notificationOccurred('success'),
        onError: () => tg?.HapticFeedback.notificationOccurred('error'),
      });
    };
    if (a === 'stop' || a === 'force_stop') {
      const msg = a === 'force_stop' ? t('vps.detail.confirmForceStop') : t('vps.detail.confirmStop');
      tg ? tg.showConfirm(msg, (ok) => ok && run()) : run();
    } else {
      run();
    }
  };

  const data = inst.data;
  const status = pickCode(data?.state) ?? pickCode(data?.status) ?? 'unknown';
  const statusDisplay =
    pickDisplay(data?.state) ?? pickDisplay(data?.status) ?? statusLabel(t, status);
  const shapeDisplay = typeof data?.shape === 'string' ? data.shape : pickDisplay(data?.shape);
  const osDisplay = pickDisplay(data?.image) ?? pickCode(data?.image);
  const osImg = data?.image?.img;
  const ipv4Raw =
    data?.connect?.primary_ipv4 ?? data?.connect?.ipv4_addresses?.[0] ?? data?.ipv4;
  const ipv4 = Array.isArray(ipv4Raw) ? ipv4Raw[0] : ipv4Raw;
  const ipv6 = data?.connect?.ipv6_networks?.[0]?.addresses?.[0];
  const isRunning = status === 'running';

  const cpuPct = metrics.data?.cpu?.percentage;
  const memPct = metrics.data?.memory?.percentage;
  const memUsage = metrics.data?.memory?.usage;
  const memLimit = metrics.data?.memory?.limit;
  const memUnit = metrics.data?.memory?.unit ?? 'MB';

  const bw = metrics.data?.bandwidth;
  // Match dashboard behavior: primary number is bandwidth.usage (egress-only,
  // the field that counts toward the quota). Convert usage to GB if it's TB.
  const bwInGB = bw?.inbound ?? 0;
  const bwOutGB = bw?.outbound ?? 0;
  const usageRaw = bw?.usage ?? bw?.outbound ?? 0;
  const bwUsageGB = bw?.usage_unit === 'TB' ? usageRaw * 1000 : usageRaw;
  const bwLimitGB = bw?.limit != null ? (bw.limit_unit === 'TB' ? bw.limit * 1000 : bw.limit) : 0;
  // Recompute pct ourselves — the backend's `percentage` is unreliable.
  const bwPct = bwLimitGB > 0 ? Math.min(100, (bwUsageGB / bwLimitGB) * 100) : 0;

  // Link speed (port cap) from /config/shape — "1 Gbps"
  const linkAmount = shape.data?.detail?.network_speed?.amount;
  const linkUnit = shape.data?.detail?.network_speed?.unit;
  const linkDisplay = linkAmount != null ? `${linkAmount} ${linkUnit ?? 'Gbps'}` : null;

  // Plan price + cycle from /config/billing
  const price = billing.data?.renew?.price;
  const cycleCode = billing.data?.pay_cycle;
  const priceDisplay =
    price != null
      ? `$${price.toFixed(2)}${cycleCode ? ` · ${cycleLabel(t, cycleCode)}` : ''}`
      : null;

  return (
    <>
      <header style={{ padding: '12px 8px 0' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '8px 12px',
            borderRadius: 'var(--r-md)',
            color: 'var(--tg-button)',
            fontSize: 15,
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={20} strokeWidth={2.4} />
          {t('common.back')}
        </button>
      </header>

      {/* Header card: name + status pill + info pills */}
      <section style={{ padding: '0 16px' }}>
        <div
          style={{
            background: 'var(--tg-section-bg)',
            borderRadius: 'var(--r-lg)',
            padding: '18px 18px 16px',
            boxShadow: 'var(--sh-1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              marginBottom: 12,
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {inst.isLoading ? <Skeleton width="70%" height={22} /> : (data?.name ?? uuid.slice(0, 8))}
            </h1>
            <StatusPill status={status} display={statusDisplay} />
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 6,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              marginLeft: -2,
              marginRight: -2,
              paddingLeft: 2,
              paddingRight: 2,
            }}
          >
            {shapeDisplay && <InfoPill icon={<Cpu size={12} />} text={shapeDisplay} />}
            {osDisplay && (
              <InfoPill
                icon={
                  osImg ? (
                    <img
                      src={osImg}
                      alt=""
                      width={14}
                      height={14}
                      style={{ display: 'block', objectFit: 'contain' }}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <OsIcon code={pickCode(data?.image) ?? ''} size={14} />
                  )
                }
                text={osDisplay}
              />
            )}
            {priceDisplay && (
              <InfoPill icon={<CircleDollarSign size={12} />} text={priceDisplay} />
            )}
          </div>
        </div>
      </section>

      {/* Power actions — compact 3-column */}
      <Section title={t('vps.detail.power')}>
        <div
          style={{
            padding: 10,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 6,
          }}
        >
          <Button
            onClick={() => trigger('stop')}
            disabled={action.isPending || !isRunning}
            variant="danger"
            leading={<PowerOff size={14} />}
            size="sm"
          >
            {t('vps.detail.power.stop')}
          </Button>
          <Button
            onClick={() => trigger('start')}
            disabled={action.isPending || isRunning}
            loading={action.isPending}
            leading={<Power size={14} />}
            size="sm"
          >
            {t('vps.detail.power.start')}
          </Button>
          <Button
            onClick={() => trigger('restart')}
            disabled={action.isPending || !isRunning}
            variant="secondary"
            leading={<RotateCcw size={14} />}
            size="sm"
          >
            {t('vps.detail.power.restart')}
          </Button>
        </div>
      </Section>

      <Section title={t('vps.detail.network')}>
        <Cell
          leading={<IpVersionBadge label="v4" color="#0284c7" />}
          title="IPv4"
          trailing={<IpCopy value={ipv4} />}
        />
        <Cell
          leading={<IpVersionBadge label="v6" color="#7c3aed" />}
          title="IPv6"
          trailing={<IpCopy value={ipv6} />}
          last
        />
      </Section>

      <Section title={t('vps.detail.realtime')}>
        <Cell
          leading={<IconBg color="#FF3B30"><Cpu size={16} color="#fff" /></IconBg>}
          title={t('vps.detail.cpu')}
          trailing={
            <span>
              {fmtPct(cpuPct)}
              {data?.connect?.cpu_cores != null && (
                <span style={{ color: 'var(--tg-hint)', marginLeft: 6 }}>
                  · {data.connect.cpu_cores}核
                </span>
              )}
            </span>
          }
        />
        <Cell
          leading={<IconBg color="#FF9500"><MemoryStick size={16} color="#fff" /></IconBg>}
          title={t('vps.detail.memory')}
          trailing={
            memUsage != null && memLimit != null
              ? `${memUsage}/${memLimit}${memUnit} · ${memPct?.toFixed(0) ?? '-'}%`
              : data?.connect?.memory != null
                ? `${data.connect.memory} MB`
                : fmtPct(memPct)
          }
        />
        <Cell
          leading={<IconBg color="#5AC8FA"><HardDrive size={16} color="#fff" /></IconBg>}
          title={t('chart.storage')}
          trailing={
            metrics.data?.disk?.usage != null && metrics.data?.disk?.limit != null
              ? `${metrics.data.disk.usage}/${metrics.data.disk.limit}${metrics.data.disk.unit ?? 'GB'} · ${metrics.data.disk.percentage ?? 0}%`
              : data?.connect?.storage != null
                ? `${data.connect.storage} GB`
                : '-'
          }
        />
        <Cell
          leading={<IconBg color="#007AFF"><Gauge size={16} color="#fff" /></IconBg>}
          title={t('vps.detail.linkSpeed')}
          trailing={linkDisplay ?? (shape.isLoading ? <Skeleton width={60} height={14} /> : '—')}
          last
        />
      </Section>

      {/* Monthly traffic — own card with progress bar */}
      <section style={{ padding: '14px 16px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: '0 16px 8px',
            gap: 8,
          }}
        >
          <h2
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--tg-section-header-text)',
              margin: 0,
            }}
          >
            {t('vps.detail.monthlyTraffic')}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {metrics.dataUpdatedAt > 0 && (
              <span style={{ fontSize: 11, color: 'var(--tg-hint)' }}>
                {t('vps.detail.updatedAgo', {
                  ago: relativeAgo(metrics.dataUpdatedAt, t),
                })}
              </span>
            )}
            <button
              onClick={() => metrics.refetch()}
              disabled={metrics.isFetching}
              aria-label={t('vps.detail.refresh')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: 8,
                background: 'transparent',
                color: 'var(--tg-button)',
                opacity: metrics.isFetching ? 0.5 : 1,
                transition: 'opacity var(--tr-fast)',
              }}
            >
              <RefreshCw
                size={14}
                style={{
                  animation: metrics.isFetching ? 'spin 1s linear infinite' : 'none',
                }}
              />
            </button>
          </div>
        </div>
        <div
          style={{
            background: 'var(--tg-section-bg)',
            borderRadius: 'var(--r-lg)',
            padding: '16px 18px',
            boxShadow: 'var(--sh-1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 10,
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <IconBg color="#5856D6"><CloudUpload size={16} color="#fff" /></IconBg>
              <span
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {fmtGB(bwUsageGB)}
                <span style={{ color: 'var(--tg-hint)', fontWeight: 500, marginLeft: 6 }}>
                  / {fmtGB(bwLimitGB)}
                </span>
              </span>
            </div>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                fontFeatureSettings: '"tnum"',
                color:
                  bwPct >= 90
                    ? 'var(--danger)'
                    : bwPct >= 70
                      ? 'var(--warning)'
                      : 'var(--success)',
              }}
            >
              {bwPct.toFixed(1)}%
            </span>
          </div>
          <div style={{ width: '100%' }}>
            <ProgressBar pct={bwPct} />
          </div>
          <div
            style={{
              display: 'flex',
              gap: 14,
              marginTop: 10,
              fontSize: 12,
              color: 'var(--tg-hint)',
              fontFeatureSettings: '"tnum"',
            }}
          >
            <span>↓ {t('vps.detail.trafficIn')} {fmtGB(bwInGB)}</span>
            <span>↑ {t('vps.detail.trafficOut')} {fmtGB(bwOutGB)}</span>
          </div>
        </div>
      </section>

      {/* Charts */}
      <section style={{ padding: '14px 16px 0' }}>
        <h2
          style={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--tg-section-header-text)',
            margin: '0 16px 8px',
          }}
        >
          {t('chart.title')}
        </h2>
        <div
          style={{
            background: 'var(--tg-section-bg)',
            borderRadius: 'var(--r-lg)',
            padding: 12,
            boxShadow: 'var(--sh-1)',
          }}
        >
          <Segmented<ChartTab>
            value={chartTab}
            onChange={setChartTab}
            options={[
              { value: 'cpu', label: t('vps.detail.cpu') },
              { value: 'memory', label: t('vps.detail.memory') },
              { value: 'live', label: t('chart.live') },
              { value: 'bandwidth', label: t('chart.bandwidth') },
            ]}
          />
          <div style={{ marginTop: 8 }}>
            {history.isLoading ? (
              <Skeleton height={160} radius={8} />
            ) : (
              <ChartPanel tab={chartTab} history={history.data} t={t} />
            )}
          </div>
        </div>
      </section>

      <Section title={t('vps.detail.timesTitle')}>
        <Cell
          leading={<IconBg color="#FF9500"><Clock size={16} color="#fff" /></IconBg>}
          title={t('vps.detail.expiresAt')}
          trailing={
            fmtDateTime(
              (data as Record<string, unknown> | undefined)?.renew_at ??
                data?.expires_at ??
                (data as Record<string, unknown> | undefined)?.expire_time ??
                billing.data?.renew?.date,
              locale,
            )
          }
          last
        />
      </Section>

    </>
  );
}

function IpCopy({ value }: { value: string | undefined }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span style={{ color: 'var(--tg-hint)' }}>—</span>;
  const onCopy = () => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        getTg()?.HapticFeedback.notificationOccurred('success');
        setTimeout(() => setCopied(false), 1400);
      })
      .catch(() => {
        getTg()?.HapticFeedback.notificationOccurred('error');
      });
  };
  return (
    <button
      onClick={onCopy}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        margin: '-4px -8px',
        borderRadius: 8,
        background: 'transparent',
        color: 'var(--tg-text)',
        transition: 'background var(--tr-fast)',
      }}
      onPointerDown={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'var(--action-active)')}
      onPointerUp={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '')}
      onPointerLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '')}
    >
      <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 13 }}>
        {value}
      </span>
      {copied ? (
        <Check size={14} color="var(--success)" />
      ) : (
        <Copy size={14} color="var(--tg-hint)" />
      )}
    </button>
  );
}

// ===== Sub-components =====

function StatusPill({ status, display }: { status: string; display: string }) {
  const bg =
    status === 'running'
      ? 'rgba(52, 199, 89, 0.16)'
      : status === 'stopped'
        ? 'rgba(142, 142, 147, 0.18)'
        : status === 'error'
          ? 'rgba(255, 59, 48, 0.16)'
          : 'rgba(255, 159, 10, 0.18)';
  const fg =
    status === 'running'
      ? '#1f9a45'
      : status === 'stopped'
        ? '#6b6b70'
        : status === 'error'
          ? 'var(--danger)'
          : '#b86b00';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: bg,
        color: fg,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <StatusDot color={fg} pulse={status === 'running'} />
      {display}
    </span>
  );
}

function InfoPill({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: 'var(--tg-secondary-bg)',
        color: 'var(--tg-text)',
        padding: '3px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <span style={{ color: 'var(--tg-hint)', display: 'inline-flex' }}>{icon}</span>
      <span>{text}</span>
    </span>
  );
}

function ChartPanel({
  tab,
  history,
  t,
}: {
  tab: ChartTab;
  history?: {
    cpu?: { x: string[]; y: number[] };
    memory?: { x: string[]; y: number[] };
    traffic?: { rx?: { x: string[]; y: number[] }; tx?: { x: string[]; y: number[] } };
    traffic_daily?: { rx?: { x: string[]; y: number[] }; tx?: { x: string[]; y: number[] } };
  };
  t: ReturnType<typeof useT>;
}) {
  if (!history) return <Empty t={t} />;
  if (tab === 'live') {
    // Rolling bandwidth in Mbps from traffic.rx/tx (~6h window, REST-polled)
    const tr = history.traffic;
    const rxObj = tr?.rx;
    const txObj = tr?.tx;
    const rxArr = rxObj?.y ?? [];
    const txArr = txObj?.y ?? [];
    if (rxArr.length === 0 && txArr.length === 0) return <Empty t={t} />;
    const xLabels = (rxObj?.x ?? txObj?.x ?? []).map(toTimeLabel);
    return (
      <SparkChart
        x={xLabels}
        series={[
          { name: t('vps.detail.in'), data: rxArr, color: '#34C759', area: true },
          { name: t('vps.detail.out'), data: txArr, color: '#007AFF', area: true },
        ]}
        unit=" Mbps"
        decimals={1}
      />
    );
  }
  if (tab === 'cpu') {
    const c = history.cpu;
    if (!c?.x?.length) return <Empty t={t} />;
    return (
      <SparkChart
        x={c.x}
        series={[{ name: 'CPU', data: c.y, color: '#FF3B30', area: true }]}
        unit="%"
      />
    );
  }
  if (tab === 'memory') {
    const m = history.memory;
    if (!m?.x?.length) return <Empty t={t} />;
    return (
      <SparkChart
        x={m.x}
        series={[{ name: 'Memory', data: m.y, color: '#FF9500', area: true }]}
        unit="%"
      />
    );
  }
  // bandwidth — daily traffic. traffic_daily.{rx,tx}.y[i] is the SUM of that day's
  // 30s-interval Mbps samples (not GB!). Convert with dashboard's formula:
  //   GB = Σmbps × 30s ÷ 8 (bit→Byte) ÷ 1024 (MB→GB)
  const td = history.traffic_daily;
  const rxObj = td?.rx;
  const txObj = td?.tx;
  const rxRaw = rxObj?.y ?? [];
  const txRaw = txObj?.y ?? [];
  if (rxRaw.length === 0 && txRaw.length === 0) return <Empty t={t} />;
  const mbpsSumToGB = (v: number) => (v * 30) / 8 / 1024;
  const rxArr = rxRaw.map(mbpsSumToGB);
  const txArr = txRaw.map(mbpsSumToGB);
  const xLabels = (rxObj?.x ?? txObj?.x ?? []).map(toDayLabel);
  return (
    <SparkChart
      x={xLabels}
      type="bar"
      series={[
        { name: t('vps.detail.in'), data: rxArr, color: '#34C759' },
        { name: t('vps.detail.out'), data: txArr, color: '#007AFF' },
      ]}
      unit=" GB"
      decimals={2}
    />
  );
}

function toTimeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function toDayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function Empty({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <div style={{ height: 160, display: 'grid', placeItems: 'center', color: 'var(--tg-hint)', fontSize: 13 }}>
      {t('common.empty')}
    </div>
  );
}

function cycleLabel(t: ReturnType<typeof useT>, code: string): string {
  const keyMap: Record<string, string> = {
    monthly: 'cycle.monthly',
    quarterly: 'cycle.quarterly',
    'semi-annually': 'cycle.semiAnnually',
    semi_annually: 'cycle.semiAnnually',
    annually: 'cycle.annually',
    'on-demand': 'cycle.onDemand',
    on_demand: 'cycle.onDemand',
    hourly: 'cycle.hourly',
  };
  const k = keyMap[code.toLowerCase()];
  return k ? t(k as Parameters<typeof t>[0]) : code;
}

function relativeAgo(ts: number, t: ReturnType<typeof useT>): string {
  if (!ts) return '';
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return t('common.justNow');
  if (s < 3600) return t('common.minutesAgo', { n: Math.floor(s / 60) });
  if (s < 86400) return t('common.hoursAgo', { n: Math.floor(s / 3600) });
  return t('common.daysAgo', { n: Math.floor(s / 86400) });
}

function fmtDateTime(v: unknown, locale: string): string {
  if (typeof v !== 'string') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pickCode(v: unknown): string | undefined {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.code === 'string') return o.code;
  }
  return undefined;
}
function pickDisplay(v: unknown): string | undefined {
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.display === 'string') return o.display;
  }
  return undefined;
}

function IpVersionBadge({ label, color }: { label: string; color: string }) {
  return (
    <div
      style={{
        width: 30,
        height: 30,
        borderRadius: 9,
        background: `${color}14`, // ~8% alpha
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.02em',
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      }}
    >
      {label}
    </div>
  );
}

function IconBg({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 13 }}>
      {children}
    </span>
  );
}

function fmtPct(v: number | undefined) {
  if (v == null) return <Skeleton width={42} height={14} />;
  return `${v.toFixed(1)}%`;
}

function fmtGB(gb: number): string {
  if (gb >= 1000) return `${(gb / 1000).toFixed(2)} TB`;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${(gb * 1000).toFixed(0)} MB`;
}
